import React, { useState } from 'react';
import styled from 'styled-components';
import { useAudio } from '../../context/AudioContext';

interface Track {
  id: string;
  title: string;
  artist: string;
  url: string;
  album_id?: string;
  album_title?: string;
  year?: number;
  genre: string[];
  description?: string;
  mood_tags: string[];
}

interface Props {
  track: Track;
  onPlay: () => void;
  onFindSimilar: () => void;
}

const TrackCardContainer = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(184, 134, 11, 0.2);
  border-radius: 8px;
  padding: 12px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: rgba(184, 134, 11, 0.4);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(184, 134, 11, 0.1);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, transparent, rgba(184, 134, 11, 0.05));
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::before {
    opacity: 1;
  }
`;

const TrackHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const TrackInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const TrackTitle = styled.h4`
  margin: 0 0 4px 0;
  font-size: 1rem;
  color: #f4e4c1;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TrackArtist = styled.p`
  margin: 0 0 4px 0;
  font-size: 0.9rem;
  color: #d4af37;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TrackMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  color: rgba(244, 228, 193, 0.7);
  margin-bottom: 8px;
`;

const TrackActions = styled.div`
  display: flex;
  gap: 8px;
  margin-left: 12px;
`;

const ActionButton = styled.button<{ primary?: boolean }>`
  padding: 6px 12px;
  border: 1px solid ${props => props.primary ? '#b8860b' : 'rgba(184, 134, 11, 0.3)'};
  background: ${props => props.primary ? 'rgba(184, 134, 11, 0.2)' : 'transparent'};
  color: ${props => props.primary ? '#f4e4c1' : 'rgba(244, 228, 193, 0.8)'};
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${props => props.primary ? 'rgba(184, 134, 11, 0.3)' : 'rgba(184, 134, 11, 0.1)'};
    border-color: #d4af37;
    color: #f4e4c1;
  }

  &:active {
    transform: translateY(1px);
  }
`;

const TrackDescription = styled.p`
  margin: 8px 0 0 0;
  font-size: 0.85rem;
  color: rgba(244, 228, 193, 0.8);
  line-height: 1.4;
  font-style: italic;
`;

const MoodTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 8px;
`;

const MoodTag = styled.span`
  background: rgba(184, 134, 11, 0.2);
  color: #d4af37;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 0.7rem;
  border: 1px solid rgba(184, 134, 11, 0.3);
`;

const PlayingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #b8860b;
  font-size: 0.8rem;
  margin-top: 4px;

  .bars {
    display: flex;
    gap: 2px;
    align-items: end;
    height: 12px;
  }

  .bar {
    width: 2px;
    background: currentColor;
    animation: musicBars 1s infinite ease-in-out;
    
    &:nth-child(1) { animation-delay: 0s; }
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }

  @keyframes musicBars {
    0%, 100% { height: 4px; }
    50% { height: 12px; }
  }
`;

export const BettyTrackCard: React.FC<Props> = ({ track, onPlay, onFindSimilar }) => {
  const { currentTrack, isPlaying } = useAudio();
  const [isHovered, setIsHovered] = useState(false);
  
  const isCurrentTrack = currentTrack?.url === track.url;
  const isCurrentlyPlaying = isCurrentTrack && isPlaying;



  const formatGenres = (genres: string[]) => {
    return genres.length > 0 ? genres.join(', ') : 'Unknown';
  };

  return (
    <TrackCardContainer
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <TrackHeader>
        <TrackInfo>
          <TrackTitle>{track.title}</TrackTitle>
          <TrackArtist>{track.artist}</TrackArtist>
          <TrackMeta>
            <span>{formatGenres(track.genre)}</span>
            {track.year && (
              <>
                <span>•</span>
                <span>{track.year}</span>
              </>
            )}
            {track.album_title && (
              <>
                <span>•</span>
                <span>{track.album_title}</span>
              </>
            )}
          </TrackMeta>
          {isCurrentlyPlaying && (
            <PlayingIndicator>
              <div className="bars">
                <div className="bar"></div>
                <div className="bar"></div>
                <div className="bar"></div>
              </div>
              Now Playing
            </PlayingIndicator>
          )}
        </TrackInfo>
        
        <TrackActions>
          <ActionButton primary onClick={onPlay}>
            {isCurrentlyPlaying ? '⏸️ Pause' : '▶️ Play'}
          </ActionButton>
          {(isHovered || window.innerWidth <= 768) && (
            <ActionButton onClick={onFindSimilar}>
              🔍 Similar
            </ActionButton>
          )}
        </TrackActions>
      </TrackHeader>

      {track.description && (
        <TrackDescription>
          {track.description}
        </TrackDescription>
      )}

      {track.mood_tags && track.mood_tags.length > 0 && (
        <MoodTags>
          {track.mood_tags.map((mood, index) => (
            <MoodTag key={index}>{mood}</MoodTag>
          ))}
        </MoodTags>
      )}
    </TrackCardContainer>
  );
};